import React, { useCallback, useEffect } from 'react';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import { BetterStackLogLevel, clientLog } from '~/lib/better-stack';
import { ClientSideSingleton as AltaPaySession } from '~/services/altapay/session';
import { CreditCardMessage } from '~/services/altapay/types';
import { PaymentMethod } from '~/shared/constants';
import { parseError } from '~/shared/utils/parse-error';
import { useTranslation } from '~/shared/utils/translation';
import {
    isAltaPayOptionLoading,
    usePaymentOptions,
} from '~/features/checkout/components/Payment/hooks';
import {
    isAltaPayScriptPayment,
    SelectablePaymentOption,
} from '~/features/checkout/components/Payment/types';
import { PaymentMessageBox } from '../MessageBox/MessageBox';
import AltaPayScriptOption from '../PaymentOption/AltaPayScriptOption';
import {
    AltaPayPaymentOption,
    AltaPayPaymentOptionProps,
    NetsPaymentOption,
} from '../PaymentOption/PaymentOption';
import { OptionsLoader, PaymentOptionLoader } from './OptionsLoader';
import styles from './PaymentOptions.module.scss';

type PaymentOptionsProps = {
    scriptUrl?: string;
    enabled?: boolean;
    shouldDisableOptions?: boolean;
    selectedOption?: SelectablePaymentOption;
    onPaymentChange(option: SelectablePaymentOption): void;
    onAltaPayScriptComplete(transactionInfo: CreditCardMessage): void;
};

export const PaymentOptions: React.FC<PaymentOptionsProps> = ({
    scriptUrl,
    enabled,
    selectedOption,
    shouldDisableOptions,
    onPaymentChange,
    onAltaPayScriptComplete,
}) => {
    const { translate } = useTranslation();
    const { basketId } = useBasket();

    const {
        options: paymentOptions,
        baseOptionsCount,
        altaPayErrors,
        altaPayOptionsError,
        altaPaySessionId,
        isLoadingAltaPay,
        hasAltaPayOptionsOnly,
    } = usePaymentOptions({ enabled });

    const hasAltaPayError = !!altaPayErrors.length;

    // Log payment options errors.
    useEffect(() => {
        if (altaPayOptionsError) {
            clientLog({
                event: 'AltaPay:Checkout:PaymentOptionsError',
                level: BetterStackLogLevel.NOTICE,
                message: `Payment options for session ${altaPaySessionId} cannot be loaded.`,
                data: {
                    error: parseError(altaPayOptionsError),
                    basketId,
                    sessionId: altaPaySessionId,
                    sessionAgeInSeconds: AltaPaySession.getSessionAgeInSeconds(),
                },
            });
        }
    }, [altaPayOptionsError]);

    const onAltaPayIframeMessageReceive = useCallback(
        (event: MessageEvent<string>) => {
            const { data } = event;

            /**
             * This is triggered during page transitions inside the AltaPay iframe.
             */
            if (data === 'redirectAway' || data === 'redirectReturn') {
                return;
            }

            /**
             * This is triggered when the credit card form is in popup mode and is closed.
             */
            if (data === 'close') {
                return;
            }

            try {
                const parsedData = JSON.parse(data);

                if (parsedData) {
                    const transaction = parsedData as CreditCardMessage;

                    onAltaPayScriptComplete(transaction);
                }
            } catch (error) {
                console.error(error);

                throw error;
            }
        },
        [onAltaPayScriptComplete],
    );

    if (isLoadingAltaPay) return <OptionsLoader count={baseOptionsCount} />;

    // We will only show the error message (from AltaPay) if there are no other payment options apart from AltaPay.
    const shouldShowAltaPayError = hasAltaPayError && hasAltaPayOptionsOnly;

    if (shouldShowAltaPayError) {
        return (
            <PaymentMessageBox
                headline={translate('checkout.payment.messages.paymentOptionsErrorTitle')}
                bodyText={translate('checkout.payment.messages.paymentOptionsErrorText')}
                cta={{
                    text: translate('checkout.payment.messages.paymentOptionsErrorRetryText'),
                    onClick: () => window.location.reload(),
                }}
            />
        );
    }

    if (!paymentOptions.length) {
        return (
            <PaymentMessageBox
                headline={translate('checkout.payment.messages.noPaymentOptionsTitle')}
                bodyText={translate('checkout.payment.messages.noPaymentOptionsText')}
                cta={{
                    text: translate('checkout.payment.messages.noPaymentOptionsRetryText'),
                    onClick: () => window.location.reload(),
                }}
            />
        );
    }

    return (
        <div className={styles.list}>
            {paymentOptions.map((option) => {
                switch (option._type) {
                    case PaymentMethod.ALTAPAY: {
                        /**
                         * If we get errors from AltaPay, we will not show its payment methods.
                         */
                        if (hasAltaPayError) return null;

                        if (isAltaPayOptionLoading(option))
                            return <PaymentOptionLoader key={option.id} />;

                        const commonProps: AltaPayPaymentOptionProps = {
                            method: option,
                            isSelected: option.id === selectedOption?.id,
                            onChange: (method) => onPaymentChange(method),
                            disabled: shouldDisableOptions,
                        };

                        if (isAltaPayScriptPayment(option)) {
                            return (
                                <AltaPayScriptOption
                                    {...commonProps}
                                    key={option.id}
                                    scriptUrl={scriptUrl}
                                    onComplete={onAltaPayIframeMessageReceive}
                                />
                            );
                        }

                        return <AltaPayPaymentOption key={option.id} {...commonProps} />;
                    }

                    case PaymentMethod.NETS: {
                        const { id, label } = option;

                        return (
                            <NetsPaymentOption
                                key={id}
                                label={label}
                                id={id}
                                isSelected={selectedOption?.id === PaymentMethod.NETS}
                                disabled={shouldDisableOptions}
                                onChange={() =>
                                    onPaymentChange({ id: PaymentMethod.NETS, name: 'Credit Card' })
                                }
                            />
                        );
                    }
                }

                return undefined;
            })}
        </div>
    );
};
